import { useEffect } from "react";
import { useCoachStore } from "@/lib/onboarding/coach-store";

/**
 * Suppress the app's persistent bottom-right floaters while a blocking
 * full-screen / docked overlay is mounted. Four floaters dock at the same
 * bottom-right slot and read this store's `overlayCount`: the onboarding coach
 * + checklist pill (z-300), the background-jobs/Downloads panel (z-300) and the
 * AI translate-progress card (z-1000). Without this they cover a modal's bottom
 * action buttons (e.g. the offering wizard's "Weiter", PExport's "Exportieren").
 * Drop `useSuppressCoach()` into any such overlay component; the ref-counted
 * store hides every floater until all registered overlays unmount. The cleanup
 * always pops, so a route change that unmounts the overlay restores them too.
 *
 * Pass `active` for overlays that stay MOUNTED while toggling visibility via a
 * prop (e.g. `if (!open) return null`): `useSuppressCoach(open)`. Overlays that
 * are conditionally mounted only while open can omit it (defaults to true).
 */
export function useSuppressCoach(active: boolean = true): void {
  useEffect(() => {
    if (!active) return;
    const { pushOverlay, popOverlay } = useCoachStore.getState();
    pushOverlay();
    return () => popOverlay();
  }, [active]);
}
